// ===========================================
// Guest photo upload
// Posts the chosen files to /api/photos/upload with a progress bar. Uses XMLHttpRequest
// rather than fetch because only XHR reports upload progress, and a guest sending twenty
// photos over a phone connection needs to see that something is happening.
// ===========================================

interface UploadResponse {
  success: boolean;
  message?: string;
  saved?: number;
  rejected?: Array<{ file: string; reason: string }>;
}

const MAX_FILES = 20;
const MAX_FILE_BYTES = 25 * 1024 * 1024;

export function initPhotoUpload(): void {
  const form = document.getElementById('photo-upload-form') as HTMLFormElement | null;
  if (!form) return;

  const input = document.getElementById('photos') as HTMLInputElement;
  const submit = document.getElementById('photo-upload-submit') as HTMLButtonElement;
  const progress = document.getElementById('photo-upload-progress') as HTMLElement;
  const barFill = document.getElementById('photo-upload-bar-fill') as HTMLElement;
  const progressText = document.getElementById('photo-upload-progress-text') as HTMLElement;
  const message = document.getElementById('photo-upload-message') as HTMLElement;
  const done = document.getElementById('photo-upload-done') as HTMLElement;
  const again = document.getElementById('photo-upload-again') as HTMLButtonElement | null;

  again?.addEventListener('click', () => {
    done.hidden = true;
    form.hidden = false;
    form.reset();
    input.focus();
  });

  form.addEventListener('submit', (e) => {
    e.preventDefault();

    const files = Array.from(input.files ?? []);
    const problem = check(files);
    if (problem) {
      showMessage(message, problem, 'error');
      return;
    }

    const data = new FormData();
    const token = form.querySelector<HTMLInputElement>('input[name="__RequestVerificationToken"]');
    if (token) data.append('__RequestVerificationToken', token.value);

    const name = (document.getElementById('uploadedBy') as HTMLInputElement | null)?.value ?? '';
    if (name.trim()) data.append('uploadedBy', name.trim());
    files.forEach((file) => data.append('photos', file, file.name));

    send(data, files.length, { form, submit, progress, barFill, progressText, message, done });
  });
}

/** Mirrors the server's limits so an obvious problem is caught before anything uploads. */
function check(files: File[]): string | null {
  if (files.length === 0) return 'Please choose at least one photo.';
  if (files.length > MAX_FILES) return `Please choose up to ${MAX_FILES} photos at a time.`;

  const tooBig = files.filter((f) => f.size > MAX_FILE_BYTES).map((f) => f.name);
  if (tooBig.length > 0) {
    return tooBig.length === 1
      ? `${tooBig[0]} is larger than 25MB, so it can't be uploaded.`
      : `${tooBig.length} of those are larger than 25MB, so they can't be uploaded.`;
  }

  return null;
}

function send(
  data: FormData,
  fileCount: number,
  el: {
    form: HTMLFormElement;
    submit: HTMLButtonElement;
    progress: HTMLElement;
    barFill: HTMLElement;
    progressText: HTMLElement;
    message: HTMLElement;
    done: HTMLElement;
  }
): void {
  const { form, submit, progress, barFill, progressText, message, done } = el;

  submit.disabled = true;
  message.hidden = true;
  progress.hidden = false;
  barFill.style.width = '0%';
  progressText.textContent = fileCount === 1 ? 'Uploading your photo…' : `Uploading ${fileCount} photos…`;

  const xhr = new XMLHttpRequest();
  xhr.open('POST', '/api/photos/upload');

  xhr.upload.addEventListener('progress', (e) => {
    if (!e.lengthComputable) return;
    const percent = Math.round((e.loaded / e.total) * 100);
    barFill.style.width = `${percent}%`;
    // At 100% the bytes are sent but the server is still filing them away.
    progressText.textContent = percent < 100 ? `Uploading… ${percent}%` : 'Almost there…';
  });

  xhr.addEventListener('load', () => {
    submit.disabled = false;
    progress.hidden = true;

    let body: UploadResponse | null = null;
    try {
      body = JSON.parse(xhr.responseText);
    } catch {
      body = null;
    }

    if (xhr.status >= 200 && xhr.status < 300 && body?.success) {
      if (body.rejected && body.rejected.length > 0) {
        const skipped = body.rejected.map((r) => `${r.file} (${r.reason})`).join(', ');
        showMessage(message, `${body.message}. These were skipped: ${skipped}`, 'warning');
      }
      form.hidden = true;
      done.hidden = false;
      done.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (xhr.status === 413) {
      showMessage(message, 'That was too much to send at once. Try a few photos at a time.', 'error');
      return;
    }

    const detail = body?.rejected?.length
      ? ` ${body.rejected.map((r) => `${r.file} (${r.reason})`).join(', ')}`
      : '';
    showMessage(message, (body?.message ?? 'Sorry, those could not be uploaded.') + detail, 'error');
  });

  xhr.addEventListener('error', () => {
    submit.disabled = false;
    progress.hidden = true;
    showMessage(message, 'The upload failed — please check your connection and try again.', 'error');
  });

  xhr.send(data);
}

function showMessage(el: HTMLElement, text: string, kind: 'error' | 'warning'): void {
  el.textContent = text;
  el.className = `photo-upload__message photo-upload__message--${kind}`;
  el.hidden = false;
}
